import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { ClearInputTextButton } from '@/components/form/baseInputs/Button/ClearInputTextButton';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Loader } from '@/components/progress/Loader/Loader';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { COLORS } from '@/styles/tokens/colors';

import { getParentSelectionState } from '../hooks/useEntityAssetOptions';
import { EntityAssetOptionGroup } from '../hooks/useHouseholdAssetsForCrossEntityDp';
import { filterAssetOptionsWithAccounts } from './AssetSelectorDropdown.utils';

interface ViewAllAssetsToDistributeSideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Asset option groups, one for each holder, in display order. See
   * buildDrawerAssetGroups.
   */
  groups: EntityAssetOptionGroup[];
  /** Composite asset values currently selected across the entire form field. */
  selectedValues: string[];
  /**
   * Commits the new selection. The caller is responsible for distributing the
   * composite values back into the typed form fields.
   */
  onApply: (values: string[]) => void;
  loading?: boolean;
}

/**
 * Side drawer used to pick assets held by the dispositioning entity or by
 * other holders in the household for an asset-level DP. The list is grouped by
 * the holder of each asset. Selections are staged locally and only committed when
 * the user clicks "Done".
 */
export function ViewAllAssetsToDistributeSideDrawer({
  isOpen,
  onClose,
  groups,
  selectedValues,
  onApply,
  loading,
}: ViewAllAssetsToDistributeSideDrawerProps) {
  const [pendingSelection, setPendingSelection] = useState<Set<string>>(
    () => new Set(selectedValues)
  );
  const [searchText, setSearchText] = useState('');

  // When the drawer is (re)opened, reset pending selection to the current
  // committed selection so we don't leak staged edits across opens.
  useEffect(() => {
    if (isOpen) {
      setPendingSelection(new Set(selectedValues));
      setSearchText('');
    }
    // We intentionally depend on isOpen so we only sync on open, not on
    // every parent-driven selectedValues change (which would discard the
    // user's in-flight edits while the drawer is open).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only on open
  }, [isOpen]);

  const filteredGroups = useMemo(() => {
    if (!searchText.trim()) return groups;
    return groups
      .map((group) => ({
        ...group,
        // Keep account parents and their holdings visible together when the
        // query matches either one.
        options: filterAssetOptionsWithAccounts(group.options, {
          inputValue: searchText,
        }),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, searchText]);

  // Toggles a single asset, or — for an account parent row — cascades to all
  // its child statics based on their current selection state. The synthetic
  // parent value itself is never added to the selection.
  const toggleOption = (option: TypeaheadSelectInputOption<string>) => {
    setPendingSelection((prev) => {
      const next = new Set(prev);
      if (option.isParentRow) {
        const children = option.childValues ?? [];
        const allSelected =
          children.length > 0 && children.every((c) => next.has(c));
        for (const child of children) {
          if (allSelected) {
            next.delete(child);
          } else {
            next.add(child);
          }
        }
      } else if (next.has(option.value)) {
        next.delete(option.value);
      } else {
        next.add(option.value);
      }
      return next;
    });
  };

  const handleDone = () => {
    onApply(Array.from(pendingSelection));
    onClose();
  };

  const body = (
    <Box width="100%">
      <Stack spacing={2}>
        <TextInput
          label="Search"
          hideLabel
          placeholder="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchSmIcon size={16} />
            </InputAdornment>
          }
          endAdornment={
            searchText ? (
              <InputAdornment position="end">
                <ClearInputTextButton onClick={() => setSearchText('')} />
              </InputAdornment>
            ) : undefined
          }
        />
      </Stack>
      <Box sx={{ pt: 1 }}>
        {loading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <Loader circularProgressProps={{ size: 32 }} />
          </Stack>
        )}
        {!loading && filteredGroups.length === 0 && (
          <Typography variant="body-xs">No assets available</Typography>
        )}
        <Stack spacing={1}>
          {filteredGroups.map((group) => {
            return (
              <Box key={group.entityId}>
                <Box sx={{ bgcolor: COLORS.NAVY[100], px: 1.5, py: 0.75 }}>
                  <Typography variant="body-xs">{group.entityName}</Typography>
                </Box>
                {group.options.map((option) => {
                  const indentLevel = option.indentLevel ?? 0;
                  let checked = pendingSelection.has(option.value);
                  let indeterminate = false;
                  if (option.isParentRow) {
                    const state = getParentSelectionState(
                      option,
                      pendingSelection
                    );
                    checked = state === 'all';
                    indeterminate = state === 'some';
                  }
                  const valueSuffix = option.endLabel
                    ? ` (${option.endLabel})`
                    : '';
                  return (
                    <Stack
                      key={option.value}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        pl: 1.5 + indentLevel * 3,
                        py: 1.5,
                        borderBottom: `1px solid ${COLORS.NAVY[100]}`,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: COLORS.GRAY[50] },
                      }}
                      onClick={() => toggleOption(option)}
                    >
                      <Typography
                        variant="body-xs"
                        sx={{ color: COLORS.TEXT.PRIMARY }}
                      >
                        {option.display}
                        {valueSuffix}
                      </Typography>
                      <Checkbox
                        hideLabel
                        label={option.display}
                        value={checked}
                        indeterminate={indeterminate}
                        // The row-level onClick owns toggling so the Checkbox
                        // stays passive; otherwise clicking the box fires both.
                        onChange={undefined}
                      />
                    </Stack>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <Button
        variant="secondary"
        size="sm"
        onClick={onClose}
        data-testid="ViewAllAssetsToDistributeSideDrawer-cancel"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleDone}
        data-testid="ViewAllAssetsToDistributeSideDrawer-done"
      >
        Done
      </Button>
    </Stack>
  );

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      heading="View all assets to distribute"
      body={body}
      footer={footer}
      bodyPropsSx={{ p: 2, flexDirection: 'column' }}
      data-testid="ViewAllAssetsToDistributeSideDrawer"
    />
  );
}
